import { useNavigate } from "react-router-dom";
import { LogIn, Sparkles } from "lucide-react";
import { lsRemove } from "../lib/safeStorage";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { useI18n } from "../lib/i18n";

/**
 * GuestGate — gentle prompt shown when a guest user tries a restricted action
 * (check-in, save, leave a review, friend-connect, etc.).
 *
 * Props:
 *   open: bool
 *   onOpenChange: (v: bool) => void
 *   action?: string  // e.g. "check in", "save this place" — flavor for the body
 */
export default function GuestGate({ open, onOpenChange, action, message, title, primaryAction = "create", returnTo }) {
  const { t } = useI18n();
  const navigate = useNavigate();

  const startGoogle = () => {
    lsRemove("habano.guest");
    // Pass `returnTo` through `/select-role` → frontend reads it and sends the
    // freshly-authed user back to their original surface (e.g. /journey).
    const target = returnTo ? `/select-role?returnTo=${encodeURIComponent(returnTo)}` : "/select-role";
    const redirectUrl = window.location.origin + target;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const goLogin = () => {
    lsRemove("habano.guest");
    onOpenChange(false);
    navigate("/login");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="guest-gate-modal"
        className="border border-gold-strong p-0 max-w-md w-[92vw] rounded-none"
        style={{ background: "#171717" }}
      >
        <DialogHeader className="px-6 pt-6 pb-3">
          <div className="w-12 h-12 grid place-items-center rounded-full border border-gold-strong bg-[rgba(232,184,75,0.08)] mb-3">
            <Sparkles className="w-5 h-5 text-gold" strokeWidth={1.5} />
          </div>
          <DialogTitle className="font-display text-2xl text-foreground leading-tight">
            {title || t("auth_join_title")}
          </DialogTitle>
          <DialogDescription className="text-sm text-foreground/65 mt-2 leading-relaxed">
            {message
              || (action
                ? t("auth_join_body").replace("{action}", `${action}, `)
                : t("auth_join_body_no_action"))}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-6 pt-2 flex flex-col gap-3">
          {primaryAction === "signin" ? (
            <>
              <button
                data-testid="guest-gate-signin"
                type="button"
                onClick={goLogin}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-gold text-[hsl(var(--primary-foreground))] hover:opacity-90 transition-opacity text-xs uppercase tracking-widest"
                style={{ minHeight: 48 }}
              >
                <LogIn className="w-4 h-4" strokeWidth={1.5} />
                {t("auth_sign_in")}
              </button>
              <button
                data-testid="guest-gate-create"
                type="button"
                onClick={startGoogle}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-gold-strong text-gold hover:bg-[rgba(232,184,75,0.08)] transition-colors text-xs uppercase tracking-widest"
                style={{ minHeight: 48 }}
              >
                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                {t("auth_create_account")}
              </button>
            </>
          ) : (
            <>
              <button
                data-testid="guest-gate-create"
                type="button"
                onClick={startGoogle}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-gold text-[hsl(var(--primary-foreground))] hover:opacity-90 transition-opacity text-xs uppercase tracking-widest"
                style={{ minHeight: 48 }}
              >
                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                {t("auth_create_account")}
              </button>
              <button
                data-testid="guest-gate-signin"
                type="button"
                onClick={goLogin}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-gold-strong text-gold hover:bg-[rgba(232,184,75,0.08)] transition-colors text-xs uppercase tracking-widest"
                style={{ minHeight: 48 }}
              >
                <LogIn className="w-4 h-4" strokeWidth={1.5} />
                {t("auth_sign_in")}
              </button>
            </>
          )}
          <button
            data-testid="guest-gate-keep-browsing"
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-xs text-foreground/55 hover:text-foreground/80 mt-1"
            style={{ minHeight: 44 }}
          >
            {t("auth_keep_browsing_guest")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
